// client/src/pages/TeacherMarksPage.js

import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Paper,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import StudentAvatar from "../../components/Parent/StudentAvatar";
import { UserContext } from "../../contexts/UserContext";
// The pass rule lives in packages/core and is generated into this copy, so the
// phone, the report card and this sheet cannot disagree about what a pass is.
import { isPass, DEFAULT_PASS_MARK } from "../../utils/parentAverages.generated";
import { fetchJson } from "../../utils/fetchJson";
import getScopeHeaders from "../../utils/scopeHeaders";
import { loadMarksSheet, selectionRequiredForClass as selectionRequiredFor } from "../../utils/marksSheet.generated";
import { sendOrQueue } from "../../utils/offlineWrite";
import { WRITE_KINDS } from "../../utils/writeOutbox";

const nurseryRubricScale = [
  { value: 1, emoji: '😢', labelKey: 'teacherMarks.nurseryScale.needsSupport', bg: '#ffebee', bd: '#e53935', text: '#b71c1c' },
  { value: 2, emoji: '😕', labelKey: 'teacherMarks.nurseryScale.developing', bg: '#fff3e0', bd: '#fb8c00', text: '#e65100' },
  { value: 3, emoji: '🙂', labelKey: 'teacherMarks.nurseryScale.satisfactory', bg: '#fffde7', bd: '#fdd835', text: '#f57f17' },
  { value: 4, emoji: '😃', labelKey: 'teacherMarks.nurseryScale.good', bg: '#f1f8e9', bd: '#7cb342', text: '#33691e' },
  { value: 5, emoji: '🌟', labelKey: 'teacherMarks.nurseryScale.excellent', bg: '#e8f5e9', bd: '#43a047', text: '#1b5e20' },
];

const renderNurseryRubricBadge = (g, size = 34) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: g.bg,
      border: `2px solid ${g.bd}`,
      color: g.text,
      fontSize: Math.round(size * 0.53),
      lineHeight: 1,
      mx: 'auto'
    }}
  >
    {g.emoji}
  </Box>
);

const levelLabel = (lvl, t) => {
  const v = (lvl || '').toString();
  if (v === 'nursery') return t('teacherMarks.levels.nursery');
  if (v === 'primary') return t('teacherMarks.levels.primary');
  if (v === 'first_cycle') return t('teacherMarks.levels.firstCycle');
  if (v === 'second_cycle') return t('teacherMarks.levels.secondCycle');
  return v || t('teacherMarks.levels.unknown');
};

export default function TeacherMarksPage() {
  const { t } = useTranslation();

  const formatWindowStatus = useCallback((status) => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'open') return t('teacherMarks.windowStatus.open');
    if (s === 'closed') return t('teacherMarks.windowStatus.closed');
    return t('teacherMarks.windowStatus.unknown');
  }, [t]);

  const formatStudentLabel = useCallback((s) => {
    const firstName = s?.firstName || '';
    const lastName = s?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || t('teacherMarks.studentWithId', { defaultValue: 'Student {{id}}', id: s?.id || '' });
  }, [t]);

  // State for dropdowns
  const [classes, setClasses] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState("");
  const [classSubjects, setClassSubjects] = useState([]); // subjects assigned to this teacher for selected class
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [sequences, setSequences] = useState([]); // [{sequenceNo, status}]
  const [seqNo, setSeqNo] = useState("");
  const [evaluationId, setEvaluationId] = useState("");
  const [resolvingEvaluationId, setResolvingEvaluationId] = useState(false);
  const [evaluationIdResolvedOnce, setEvaluationIdResolvedOnce] = useState(false);

  const selectedClassObj = useMemo(
    () => (classes || []).find((c) => String(c.id) === String(selectedClass)) || null,
    [classes, selectedClass]
  );

  const selectedEvaluation = useMemo(
    () => (evaluations || []).find((ev) => String(ev.id) === String(selectedEvaluationId)) || null,
    [evaluations, selectedEvaluationId]
  );

  const levelOptions = useMemo(() => {
    const set = new Set((classes || []).map((c) => (c?.level || '').toString()).filter(Boolean));
    return Array.from(set);
  }, [classes]);

  const filteredClasses = useMemo(() => {
    if (!selectedLevel) return classes || [];
    return (classes || []).filter((c) => String(c?.level || '') === String(selectedLevel));
  }, [classes, selectedLevel]);

  const displayClassName = (c) => {
    const raw = (c?.name || c?.className || '').toString();
    if (!raw) return t('teacherMarks.classWithId', { id: c?.id });
    return raw.replace(/\s*-\s*N\/A\s*$/i, '').trim();
  };

  const formatEvaluationLabel = useCallback((ev) => {
    if (!ev) return t('teacherMarks.evaluation', { defaultValue: 'Evaluation' });
    if (ev.name) return ev.name;
    if (ev.type === 'sequence' && ev.sequenceNo) return `${t('teacherMarks.sequenceLabel')} ${ev.sequenceNo}`;
    if (ev.type === 'mock') return `Mock ${ev.mockNo || ev.number || ''}`.trim();
    return `${t('teacherMarks.evaluation', { defaultValue: 'Evaluation' })} ${ev.id}`;
  }, [t]);

  const isNurseryMode = useMemo(() => {
    const lvl = (selectedClassObj?.level || '').toString();
    return lvl === 'nursery';
  }, [selectedClassObj]);

  // Shared with the phone, via the generated copy. This rule decides whether an
  // empty elective roster is a real answer ("nobody chose this subject") or a
  // sign the electives table was never filled — and the two clients answering it
  // separately is how they came to disagree about who is in a marks sheet.
  const selectionRequiredForClass = useMemo(
    () => selectionRequiredFor(selectedClassObj),
    [selectedClassObj]
  );

  // State for students & marks
  const [students, setStudents] = useState([]);
  const [marksMap, setMarksMap] = useState({}); // { studentId: existingMark }
  const [scores, setScores] = useState({}); // { studentId: enteredScore }
  const [scoreErrors, setScoreErrors] = useState({});

  // THE SCHOOL'S OWN PASS MARK, not the default. A teacher colouring marks
  // against a hard-coded 10 would contradict whatever the school actually set
  // in Evaluation Control Centre. DEFAULT_PASS_MARK applies until the answer
  // arrives, and for a school that has never set one.
  // THE SCHOOL'S OWN PASS MARK, off the session rather than fetched.
  //
  // It lives on the school record, but the endpoint that serves it sits behind
  // page access for school settings, payments or finance — none of which an
  // ordinary teacher holds, so asking for it there would 403 for exactly the
  // people who spend most time on this screen, and the sheet would silently
  // colour against 10 while the school had set something else.
  //
  // /users/me carries it instead: loaded once at startup, school-scoped, and
  // already in context here. DEFAULT_PASS_MARK is the fallback for a school
  // that has never set one.
  const sessionUser = useContext(UserContext);
  const passMark = useMemo(() => {
    const v = Number(sessionUser?.school?.passAverage);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_PASS_MARK;
  }, [sessionUser]);

  /**
   * Green at or above the pass mark, red below, nothing when empty.
   *
   * Text colour and weight only — the outline belongs to `error`, which says
   * "this is not a valid mark", and that must not be drowned out by a verdict
   * on a number that was never accepted.
   */
  const markSx = useCallback((value) => {
    const verdict = isPass(value, passMark);
    if (verdict === null) return undefined;
    return { '& input': { color: verdict ? 'success.main' : 'error.main', fontWeight: 700 } };
  }, [passMark]);
  const [ratings, setRatings] = useState({}); // { studentId: enteredRating }
  const [ratingErrors, setRatingErrors] = useState({});

  // Loading & feedback
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  // Fetch classes on mount (only classes relevant to this teacher)
  useEffect(() => {
    async function loadClasses() {
      try {
        const data = await fetchJson(`/teacher/classes?_t=${Date.now()}`);
        let list = Array.isArray(data) ? data : (data?.classes || []);
        // Fallback to school-phase classes if teacher endpoint returns empty
        if (!list || list.length === 0) {
          const alt = await fetchJson(`/api/allocations/classes`).catch(() => []);
          list = Array.isArray(alt) ? alt : (alt?.classes || []);
        }
        setClasses(list || []);
      } catch (err) {
        console.error(err);
      }
    }
    loadClasses();
    const onFocus = () => loadClasses();
    const onContextChange = () => loadClasses();
    window.addEventListener('focus', onFocus);
    window.addEventListener('app:phase:changed', onContextChange);
    window.addEventListener('app:ay:changed', onContextChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('app:phase:changed', onContextChange);
      window.removeEventListener('app:ay:changed', onContextChange);
    };
  }, [t]);

  // Load sequences (window states) from teacher-safe status endpoint
  useEffect(() => {
    async function loadSeqs() {
      try {
        // Ensure we pass the same context the admin uses in Setup, so status stays in sync.
        // ONE HELPER, because this block resolved the school id as
        // `schoolId || activeSchoolId || …` while the two below resolved it as
        // `activeSchoolId || … || schoolId`. The academic year is stored under a
        // school-namespaced key, so when those disagree this page read sequence
        // status for one year and saved marks against another.
        const data = await fetchJson('/api/academics/evaluations/sequences/status', { headers: getScopeHeaders() });
        const list = (data?.sequences || []).map(x => ({ sequenceNo: x.sequenceNo || x.number || x.seq || x, status: x.status || 'closed' }));
        setSequences(list);
        setSeqNo((prev) => {
          if (prev && list.some((item) => String(item.sequenceNo) === String(prev))) return prev;
          return list.length ? String(list[0].sequenceNo) : '';
        });
      } catch (_) {
        setSequences([1,2,3,4,5,6].map(n => ({ sequenceNo: n, status: 'closed' })));
      }
    }
    loadSeqs();
    const onFocus = () => loadSeqs();
    const onContextChange = () => loadSeqs();
    const intervalId = window.setInterval(loadSeqs, 30000);
    window.addEventListener('focus', onFocus);
    window.addEventListener('app:phase:changed', onContextChange);
    window.addEventListener('app:ay:changed', onContextChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('app:phase:changed', onContextChange);
      window.removeEventListener('app:ay:changed', onContextChange);
    };
  }, [t]);

  // When selectedClass changes, fetch subjects assigned to this teacher for that class
  useEffect(() => {
    async function loadClassSubjects() {
      if (!selectedClass) {
        setClassSubjects([]);
        setSelectedSubjectId("");
        return;
      }
      try {
        const data = await fetchJson(`/teacher/classes/${encodeURIComponent(selectedClass)}/subjects`);
        const subjects = Array.isArray(data) ? data : [];
        setClassSubjects(subjects);
        if (subjects.length === 1) {
          setSelectedSubjectId(subjects[0]?.id || "");
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadClassSubjects();
  }, [selectedClass, t]);

  // Load class-assigned evaluation options
  useEffect(() => {
    async function loadEvaluations() {
      if (!selectedClass) {
        setEvaluations([]);
        setSelectedEvaluationId("");
        setEvaluationId("");
        setSeqNo("");
        setEvaluationIdResolvedOnce(false);
        return;
      }
      try {
        setResolvingEvaluationId(true);
        setEvaluationIdResolvedOnce(false);
        const r = await fetchJson(`/api/academics/evaluations/class/${encodeURIComponent(selectedClass)}/evaluations`, { headers: getScopeHeaders() });
        const list = Array.isArray(r) ? r : (r?.evaluations || []);
        setEvaluations(list);
        setSelectedEvaluationId((prev) => {
          if (prev && list.some((ev) => String(ev.id) === String(prev))) return String(prev);
          return list[0]?.id ? String(list[0].id) : "";
        });
        setEvaluationIdResolvedOnce(true);
      } catch (_) {
        setEvaluations([]);
        setSelectedEvaluationId("");
        setEvaluationId("");
        setSeqNo("");
        setEvaluationIdResolvedOnce(true);
      } finally {
        setResolvingEvaluationId(false);
      }
    }
    loadEvaluations();
    const onFocus = () => loadEvaluations();
    const onContextChange = () => loadEvaluations();
    const intervalId = window.setInterval(() => {
      if (selectedClass) loadEvaluations();
    }, 30000);
    window.addEventListener('focus', onFocus);
    window.addEventListener('app:phase:changed', onContextChange);
    window.addEventListener('app:ay:changed', onContextChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('app:phase:changed', onContextChange);
      window.removeEventListener('app:ay:changed', onContextChange);
    };
  }, [selectedClass]);

  useEffect(() => {
    const ev = (evaluations || []).find((row) => String(row.id) === String(selectedEvaluationId)) || null;
    setEvaluationId(ev?.id ? String(ev.id) : "");
    setSeqNo(ev?.sequenceNo ? String(ev.sequenceNo) : "");
  }, [evaluations, selectedEvaluationId]);

  // Load students & existing marks when ready
  useEffect(() => {
    async function loadStudentsAndMarks() {
      if (!selectedClass || !selectedSubjectId || !evaluationId) return;

      setLoading(true);
      setStudents([]);
      setMarksMap({});
      setScores({});
      setRatings({});

      try {
        // ONE routine, shared with the phone. It was this block, duplicated by
        // hand, that let the two clients disagree: the phone had no fallback to
        // the class list, so the same teacher opening the same subject saw a
        // full sheet here and an empty screen there.
        //
        // The hand-built context headers that used to sit here are gone because
        // fetchJson already attaches school, phase and academic year — this was
        // re-deriving from localStorage what the transport had all along.
        const { students: studentData, marksById: map, values } =
          await loadMarksSheet({
            classId: selectedClass,
            subjectId: selectedSubjectId,
            evaluationId,
            sequenceNo: seqNo,
            nursery: isNurseryMode,
            selectionRequired: selectionRequiredForClass,
          });

        const initialScores = isNurseryMode ? {} : values;
        const initialRatings = isNurseryMode ? values : {};

        setStudents(studentData);
        setMarksMap(map);
        setScores(initialScores);
        setRatings(initialRatings);
        setScoreErrors({});
        setRatingErrors({});
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadStudentsAndMarks();
  }, [selectedClass, selectedSubjectId, seqNo, evaluationId, t, selectionRequiredForClass, isNurseryMode]);

  // Handle input change for score
  const handleScoreChange = (studentId, value) => {
    setScores((prev) => ({ ...prev, [studentId]: value }));
    setScoreErrors((prev) => {
      if (!prev || !prev[studentId]) return prev;
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };

  const normalizeScore = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return n;
  };

  const clampAndFormatScore = (raw) => {
    const n = normalizeScore(raw);
    if (n === null) return '';
    if (!Number.isFinite(n)) return null;
    const clamped = Math.max(0, Math.min(20, n));
    return clamped.toFixed(1);
  };

  const handleScoreBlur = (studentId) => {
    setScores((prev) => {
      const raw = prev[studentId];
      if (raw === '' || raw === null || raw === undefined) return { ...prev, [studentId]: '' };
      const formatted = clampAndFormatScore(raw);
      return { ...prev, [studentId]: (formatted ?? '') };
    });
  };

  const handleRatingSelect = (studentId, value) => {
    setRatings((prev) => ({ ...prev, [studentId]: String(value) }));
    setRatingErrors((prev) => {
      if (!prev || !prev[studentId]) return prev;
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };

  // Save all marks (bulk upload/upsert)
  const handleSaveAll = async () => {
    setSaveError("");
    setSaveMessage("");
    setLoading(true);

    try {
      if (isNurseryMode) {
        const nextErrors = {};
        for (const s of (students || [])) {
          const n = Number(ratings[s.id]);
          if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 5) nextErrors[s.id] = t('teacherMarks.validation.selectRating');
        }
        setRatingErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
          setSaveError(t('teacherMarks.validation.fixInvalidRatings'));
          setLoading(false);
          return;
        }
      } else {
        const nextErrors = {};
        for (const s of (students || [])) {
          const n = normalizeScore(scores[s.id]);
          if (n === null || !Number.isFinite(n)) nextErrors[s.id] = t('teacherMarks.validation.invalidScore');
          else if (n < 0 || n > 20) nextErrors[s.id] = t('teacherMarks.validation.scoreRange', { min: 0, max: 20 });
        }
        setScoreErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
          setSaveError(t('teacherMarks.validation.fixInvalidScores'));
          setLoading(false);
          return;
        }
      }

      const payload = isNurseryMode
        ? {
            classId: selectedClass,
            subjectId: selectedSubjectId,
            evaluationId,
            marks: students.map((s) => ({
              studentId: s.id,
              rating: Number(ratings[s.id]),
            })),
          }
        : {
            classId: selectedClass,
            subjectId: selectedSubjectId,
            evaluationId,
            marks: students.map((s) => ({
              studentId: s.id,
              score: Number(normalizeScore(scores[s.id]).toFixed(1)),
            })),
          };
      // THE SAME SCOPE THE SHEET WAS READ WITH. This is a WRITE, and it is
      // queued offline, so a header resolved differently here than in the two
      // loaders above files marks against a different academic year than the
      // one on screen — and the queue replays it later, when nothing is left to
      // notice with.
      const headers = {
        "Content-Type": "application/json",
        ...getScopeHeaders(),
      };
      const saveEndpoint = isNurseryMode ? '/api/nursery-marks/bulk' : '/api/marks/bulk';
      const { queued } = await sendOrQueue({
        kind: WRITE_KINDS.MARKS,
        endpoint: saveEndpoint,
        method: 'POST',
        headers,
        body: payload,
        label: t('teacherMarks.pageTitle'),
      });
      // Queued means stored locally, not saved — say so rather than claiming success.
      setSaveMessage(queued ? t('offlineWrites.queued') : t("teacherMarks.saveSuccess"));
    } catch (err) {
      console.error('Failed to save marks:', err);
      setSaveError(t('teacherMarks.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const hasExistingMarks = useMemo(() => {
    try {
      return students.some((s) => !!marksMap[String(s.id)]);
    } catch (_) {
      return false;
    }
  }, [students, marksMap]);

  const getEvaluationWindowStatus = useCallback((ev) => {
    if (!ev) return '';
    if (ev.type === 'sequence') {
      const sequenceValue = ev.sequenceNo || ev.number || '';
      const match = sequences.find((row) => String(row.sequenceNo) === String(sequenceValue));
      return match?.status || 'closed';
    }
    return ev.state || '';
  }, [sequences]);

  const selectedEvaluationStatus = useMemo(() => {
    if (!selectedEvaluation) return '';
    return getEvaluationWindowStatus(selectedEvaluation);
  }, [selectedEvaluation, getEvaluationWindowStatus]);

  const gatingClosed = useMemo(() => !!selectedEvaluation && selectedEvaluationStatus !== 'open', [selectedEvaluation, selectedEvaluationStatus]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 0, sm: 2, md: 3 }, py: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" gutterBottom>
        {t('teacherMarks.pageTitle')}
      </Typography>

      {/* Dropdowns aligned with requested order: Level → Class → Evaluation → Subject */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, borderRadius: { xs: 0, sm: 2 } }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: { xs: 0, sm: 200 }, flex: { xs: '1 1 100%', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
            <InputLabel id="level-select-label">{t('teacherMarks.level')}</InputLabel>
            <Select
              labelId="level-select-label"
              value={selectedLevel}
              label={t('teacherMarks.level')}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setSelectedClass("");
                setSelectedEvaluationId("");
                setSelectedSubjectId("");
                setEvaluationId("");
                setSeqNo("");
              }}
            >
              <MenuItem value=""><em>{t('teacherMarks.allLevels')}</em></MenuItem>
              {levelOptions.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>{levelLabel(lvl, t)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Class */}
          <FormControl sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: '1 1 100%', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
            <InputLabel id="class-select-label">
              {t('teacherMarks.selectClass')}
            </InputLabel>
            <Select
              labelId="class-select-label"
              value={selectedClass}
              label={t('teacherMarks.selectClass')}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedEvaluationId("");
                setSelectedSubjectId("");
                setEvaluationId("");
                setSeqNo("");
              }}
            >
              {filteredClasses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {displayClassName(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: '1 1 100%', sm: 1 }, width: { xs: '100%', sm: 'auto' } }} disabled={!selectedClass}>
            <InputLabel id="evaluation-select-label">{t('teacherMarks.evaluation', { defaultValue: 'Evaluation' })}</InputLabel>
            <Select
              labelId="evaluation-select-label"
              value={selectedEvaluationId}
              label={t('teacherMarks.evaluation', { defaultValue: 'Evaluation' })}
              onChange={(e) => {
                setSelectedEvaluationId(e.target.value);
              }}
            >
              {evaluations.map((ev) => (
                <MenuItem key={ev.id} value={String(ev.id)}>
                  {formatEvaluationLabel(ev)}{getEvaluationWindowStatus(ev) ? ` (${formatWindowStatus(getEvaluationWindowStatus(ev))})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Subject (filtered to teacher's subjects) */}
          <FormControl sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: '1 1 100%', sm: 1 }, width: { xs: '100%', sm: 'auto' } }} disabled={!selectedClass || !selectedEvaluationId}>
            <InputLabel id="subject-select-label">
              {t('teacherMarks.selectSubject')}
            </InputLabel>
            <Select
              labelId="subject-select-label"
              value={selectedSubjectId}
              label={t('teacherMarks.selectSubject')}
              onChange={(e) => {
                setSelectedSubjectId(e.target.value);
              }}
            >
              {classSubjects.map((subj) => (
                <MenuItem key={subj.id} value={subj.id}>
                  {subj.name || t('common.subject')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Feedback */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}
      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}
      {/* Hint when no evaluation is configured for selected class+sequence */}
      {selectedClass && evaluations.length === 0 && evaluationIdResolvedOnce && !resolvingEvaluationId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('teacherMarks.noEvaluationHint')}
        </Alert>
      )}

      {/* Marks Entry (aligned to Control Center format: per-student table) */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        selectedClass && selectedSubjectId && evaluationId && (
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, borderRadius: { xs: 0, sm: 2 } }}>
            {selectionRequiredForClass && students.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('teacherMarks.noRosterForSubject')}
              </Alert>
            )}
            {/* A missing mark is SKIPPED by every average in this product: sequence to term and term to annual both drop what is absent and divide by what is there. So a child who sat nothing is averaged on nothing and comes out looking like one who sat everything, and a subject left entirely blank disappears from the report card rather than showing a gap.
            
                Nothing writes a zero on anybody's behalf. A 0 counts against a child and only the teacher knows whether an absence deserves it.
            
                Never on the nursery rubric: that scale is 1 to 5 and has no zero, so the advice would be refused by the very field it is printed above. */}
            {students.length > 0 && !isNurseryMode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('teacherMarks.zeroTip', {
                  defaultValue: 'Enter 0 for any student who was not evaluated. A blank is skipped by the averages rather than counted as nothing, so leaving it empty quietly drops the subject from that student\'s report card.',
                })}
              </Alert>
            )}
            {students.length > 0 && (() => {
              const total = students.length;
              const enteredCount = students.filter(s => !!marksMap[String(s.id)]).length;
              const pendingCount = total - enteredCount;
              const errorCount = Object.keys(isNurseryMode ? ratingErrors : scoreErrors).length;
              const recorders = new Map();
              for (const s of students) {
                const existing = marksMap[String(s.id)];
                if (!existing) continue;
                const name = existing?.Recorder?.name || t('common.unknown', 'Unknown');
                const whenRaw = existing?.updatedAt || existing?.createdAt || null;
                const when = whenRaw ? new Date(whenRaw) : null;
                if (!recorders.has(name)) recorders.set(name, { name, when, count: 0 });
                recorders.get(name).count++;
              }
              const recorderEntries = [...recorders.values()];
              return (
                <Alert severity={errorCount > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" component="span"><strong>{t('teacherMarks.banner.total', { count: total, defaultValue: `Total: ${total}` })}</strong></Typography>
                    <Typography variant="body2" component="span" color="success.main">{t('teacherMarks.banner.entered', { count: enteredCount, defaultValue: `Entered: ${enteredCount}` })}</Typography>
                    {pendingCount > 0 && <Typography variant="body2" component="span" color="text.secondary">{t('teacherMarks.banner.pending', { count: pendingCount, defaultValue: `Pending: ${pendingCount}` })}</Typography>}
                    {errorCount > 0 && <Typography variant="body2" component="span" color="error.main">{t('teacherMarks.banner.errors', { count: errorCount, defaultValue: `Errors: ${errorCount}` })}</Typography>}
                  </Box>
                  {recorderEntries.map((e, i) => {
                    const whenText = e.when && !Number.isNaN(e.when.getTime()) ? e.when.toLocaleString() : '';
                    return (
                      <Typography key={i} variant="body2" component="div">
                        {t('teacherMarks.banner.recordedBy', { count: e.count, name: e.name, defaultValue: `${e.count} mark(s) recorded by ${e.name}` })}{whenText ? ` — ${whenText}` : ''}
                      </Typography>
                    );
                  })}
                </Alert>
              );
            })()}
            {!isNurseryMode ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, width: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 0, sm: 400 }, tableLayout: { xs: 'fixed', sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      {/* The name carries a face as well as text now, and the
                          score beside it is at most four characters — "12.5".
                          200px to hold that was width taken from the only
                          column anybody actually reads. */}
                      <TableCell sx={{ width: { xs: '70%', sm: 'auto' } }}><strong>{t('teacherMarks.student')}</strong></TableCell>
                      <TableCell sx={{ width: { xs: '30%', sm: 110 }, px: { xs: 0.75, sm: 1 } }}><strong>{t('teacherMarks.score', { defaultValue: 'Score (/20)' })}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell sx={scoreErrors[s.id] ? { borderLeft: '3px solid', borderLeftColor: 'error.main', whiteSpace: 'normal', wordBreak: 'break-word' } : { whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {/* A face beside the name: the teacher is matching
                              each row to a child they know by sight, and a
                              column of names alone makes that a reading
                              exercise. Photo where the school has one, a boy or
                              a girl where it has not. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
                            <StudentAvatar student={s} size={28} sx={{ flexShrink: 0 }} />
                            <Box component="span" sx={{ minWidth: 0 }}>
                              {`${s.firstName || ''} ${s.lastName || ''}`.trim() || formatStudentLabel(s)}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 0.75, sm: 1 } }}>
                          <TextField
                            fullWidth
                            type="number"
                            value={scores[s.id] ?? ""}
                            onChange={(e) => handleScoreChange(s.id, e.target.value)}
                            onBlur={() => handleScoreBlur(s.id)}
                            inputProps={{ min: 0, max: 20, step: 0.1 }}
                            size="small"
                            disabled={gatingClosed}
                            error={!!scoreErrors[s.id]}
                            helperText={scoreErrors[s.id] || ''}
                            sx={{
                              // NO FLOOR ON A PHONE. The cell is a percentage of
                              // a fixed layout, so on a 320px screen it is about
                              // 96px — a floor fits, but it is a floor competing
                              // with the very thing that makes the row fit, and
                              // on the next narrower device it wins and the
                              // scrollbar comes back. The percentage is enough.
                              minWidth: { xs: 0, sm: 90 },
                              '& .MuiInputBase-input': {
                                px: { xs: 0.75, sm: 1 },
                                py: { xs: 1, sm: 1.25 },
                                fontSize: { xs: '0.95rem', sm: '1rem' }
                              },
                              // PASS IN GREEN, FAIL IN RED, AS IT IS TYPED. A
                              // teacher entering thirty marks is looking for the
                              // failures, and finding them meant checking every
                              // number against a threshold held in their head.
                              // Bold as well as coloured: red and green is the
                              // one pair a colourblind reader cannot separate,
                              // so weight carries the signal too.
                              ...markSx(scores[s.id]),
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                {/* NO FORCED 560px ON A PHONE. Six columns behind a horizontal
                    scroll means the teacher drags the table left to see the
                    rubric, then drags it right to read who they are marking —
                    for every child in the class. A fixed layout at 40% for the
                    name and 12% for each of the five points fits a 360px screen,
                    and the badges are legible at that size because they are
                    glyphs rather than words. */}
                <Table
                  size="small"
                  sx={{
                    minWidth: { xs: 0, sm: 560 },
                    tableLayout: { xs: 'fixed', sm: 'auto' },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: { xs: '40%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                        <strong>{t('teacherMarks.student')}</strong>
                      </TableCell>
                      {nurseryRubricScale.map((g) => (
                        <TableCell key={g.value} align="center" sx={{ backgroundColor: g.bg, py: 1.25, width: { xs: '12%', sm: 'auto' }, px: { xs: 0.25, sm: 2 } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1, gap: 0.5 }}>
                            {renderNurseryRubricBadge(g)}
                            <Typography variant="caption" sx={{ color: g.text, fontWeight: 700, display: 'block', textAlign: 'center', lineHeight: 1.1 }}>{g.value}</Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((s) => {
                      const label = formatStudentLabel(s);
                      const current = Number(ratings[s.id]);
                      return (
                        <TableRow key={s.id}>
                          <TableCell sx={{ px: { xs: 0.75, sm: 2 }, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
                              <StudentAvatar student={s} size={28} sx={{ flexShrink: 0 }} />
                              <Box component="span" sx={{ minWidth: 0 }}>{label}</Box>
                            </Box>
                            {ratingErrors[s.id] && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {ratingErrors[s.id]}
                              </Typography>
                            )}
                          </TableCell>
                          {nurseryRubricScale.map((g) => {
                            const selected = Number.isFinite(current) && current === g.value;
                            return (
                              <TableCell
                                key={g.value}
                                align="center"
                                onClick={() => { if (!gatingClosed) handleRatingSelect(s.id, g.value); }}
                                sx={{
                                  px: { xs: 0.25, sm: 2 },
                                  cursor: gatingClosed ? 'not-allowed' : 'pointer',
                                  backgroundColor: selected ? g.bd : g.bg,
                                  border: selected ? `2px solid ${g.bd}` : '1px solid rgba(0,0,0,0.08)',
                                  color: selected ? '#fff' : g.text,
                                  fontWeight: 800,
                                  userSelect: 'none'
                                }}
                              >
                                {selected ? '✓' : ''}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )
      )}

      {/* Save Button */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 1, px: { xs: 1, sm: 0 } }}>
        <Button
          variant="contained"
          disabled={
            !selectedClass ||
            !selectedEvaluationId ||
            !selectedSubjectId ||
            !evaluationId ||
            students.length === 0 ||
            gatingClosed
          }
          onClick={handleSaveAll}
        >
          {hasExistingMarks
            ? t('teacherMarks.editMarks')
            : t('teacherMarks.saveAll')}
        </Button>
        {selectedEvaluation && selectedEvaluationStatus && (
          <Alert severity={selectedEvaluationStatus === 'open' ? 'success' : 'info'} sx={{ py: 0.25 }}>
            {t('teacherMarks.window')}: {formatWindowStatus(selectedEvaluationStatus)}
          </Alert>
        )}
      </Box>
      {gatingClosed && (
        <Typography variant="caption" color="text.secondary" sx={{ px: { xs: 1, sm: 0 }, display: 'block', mt: 1 }}>
          {t('teacherMarks.windowClosed')}
        </Typography>
      )}
    </Box>
  );
}
